import { resolveDesignProfile } from "../design-profiles";
import { batteryRangeDischargeLimit, declaredSourceVoltage, detectCombinedBatteryDischarge } from "./battery-discharge";
import { chargeAdviceBasis, chargeAdviceThreshold } from "../charge-advice-basis";
import { outputPortCurrent } from "../output-current-limits";
import { makeSystemCableSpecGetter } from "../cable-spec";
import { applyFuseBoardCableLimits } from "../fuse-board-limits";
import { currentNeedsVerification, sustainedCableCurrent } from "../cable-current-evidence";
import { batteryCurrentShares, computeBatterySourceRanges, type BatteryStack } from "../battery-stack";
import { dcChargingPortGroups, isBatteryPoweredInverter, isDirectDcSource } from "../component-ports";
import { buildDcCircuitTopology, dcInputPorts } from "../dc-circuit-topology";
import { detectChargerPathVDrop } from "./charger-vdrop";
import { detectInstallationLimits } from "./installation";
import {
  getEffectiveCableFuse,
  OVERLOAD_TOLERANCE,
  type CableSpec,
  type SimulateContext,
} from "../cable-current";
import type { DefLookup } from "../catalog";
import { computeCableDemandAnalysis, computeCableDemands } from "../cable-demands";
export { computeCableDemands, type CableDemand } from "../cable-demands";
import { diagnosticLabel } from "../i18n/diagnostic-label";
import { resolveCablePolarity } from "../cable-color";
import { indexNodesById } from "../node-tree";
import { collectLoadCircuits, type LoadCircuit } from "../load-circuits";
import { computePvCurrentAnalysis } from "../pv-currents";
import { collectCrankingCircuits } from "../cranking-circuit";
import { DEFAULT_AUTO_SIZE_VDROP_TARGET } from "../settings/auto-size-vdrop-default";
import { findCable, findNode } from "../system-lookup";
import { SYSTEM_BUS_V } from "../cable-voltage";
import { computeIsolatorModelBoundary } from "../dc-isolators";
import { computeVdropAllowances } from "../vdrop-allowance";
import type {
  Cable,
  ComponentDefinition,
  Issue,
  System,
  Terminal,
} from "../types";
import { recommendFuse, voltageDrop } from "../wire-tables";
import {
  buildGraph,
  reachable,
  splitV,
  v,
  type Vertex,
} from "./graph";

/**
 * Compare inverter demand with declared discharge ratings; missing ratings
 * require verification, not a chemistry-derived maximum.
 */
export function detectInverterVsBattery(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const definitions = new Map(system.nodes.flatMap(node => {
    if (node.kind !== "component") return [];
    const def = defLookup(node.definitionId, node.customDefinition);
    return def ? [[node.id, def] as const] : [];
  }));
  const topology = buildDcCircuitTopology(system, defLookup);
  const ports = dcInputPorts(system, defLookup);
  const isolator = computeIsolatorModelBoundary(system, defLookup);
  const issues: Issue[] = [];
  for (const n of system.nodes) {
    if (n.kind !== "component") continue;
    const def = definitions.get(n.id);
    if (!def || !isBatteryPoweredInverter(def)) continue;
    // Rate the battery that supplies both DC rails of this input. A common
    // negative, an AC connection, or another voltage domain cannot supply it.
    const inputs = ports.filter(port => port.nodeId === n.id);
    if (inputs.length !== 1) continue;
    const resolution = topology.resolvePort(inputs[0]);
    if (resolution.unsupported.length) continue;
    const routes = resolution.routes.filter(route => route.source.kind === "battery");
    if (!routes.length || routes.some(route => [...route.positive, ...route.negative, ...route.series]
      .some(step => isolator.cableIds.has(step.cableId)))) continue;
    const sources = [...new Map(routes.map(route => [route.sourceId, route.source])).values()];
    const voltages = sources.map(source => declaredSourceVoltage(source, definitions));
    const busV = voltages[0];
    const sameVoltage = busV != null && voltages.every(voltage => voltage === busV);
    const inverterDcDraw = def.ampDraw ?? (sameVoltage && def.watts ? def.watts / busV / 0.92 : 0);
    if (!(inverterDcDraw > 0) || !Number.isFinite(inverterDcDraw)) continue;
    const limits = sources.map(source => batteryRangeDischargeLimit(source, definitions));
    const ids = sources.flatMap(source => source.batteryNodeIds);
    // Ambiguous overlapping strings or unequal voltages cannot establish a
    // summed bank rating. The combined check can still prove a mandatory limit.
    const totalMaxDischarge = (sources.length === 1 || (sameVoltage && new Set(ids).size === ids.length)) &&
      limits.every(limit => limit != null) ? limits.reduce((sum, limit) => sum + limit, 0) : undefined;
    if (totalMaxDischarge != null && inverterDcDraw > totalMaxDischarge) {
      const label = n.label ?? def.shortName ?? def.name;
      issues.push({
        code: "inverter-exceeds-battery-c-rate",
        severity: "critical",
        title: {
          key: "diag.inverter-exceeds-battery-c-rate.title",
          vars: { label },
        },
        message: {
          key: "diag.inverter-exceeds-battery-c-rate.message",
          vars: {
            label,
            draw: Math.round(inverterDcDraw),
            bank: Math.round(totalMaxDischarge),
          },
        },
        nodeIds: [n.id],
        fix: { key: "diag.inverter-exceeds-battery-c-rate.fix" },
      });
    } else if (totalMaxDischarge == null) {
      const label = n.label ?? def.shortName ?? def.name;
      issues.push({
        code: "inverter-exceeds-battery-c-rate",
        severity: "warning",
        title: { key: "diag.inverter-exceeds-battery-c-rate.title_unverified", vars: { label } },
        message: { key: "diag.inverter-exceeds-battery-c-rate.message_unverified", vars: { label, draw: Math.round(inverterDcDraw) } },
        nodeIds: [n.id],
        fix: { key: "diag.inverter-exceeds-battery-c-rate.fix_unverified" },
      });
    }
  }
  return issues;
}

/**
 * Loading more than ~50 % of an alternator's rated continuous output stresses
 * the diodes and overheats it at idle.
 */
/** A charger that takes DC in and puts DC out — the only kind an alternator
 *  can be asked to feed. Decided from the terminals, never from the id: matching
 *  `"renogy"` once counted a Renogy Wanderer *MPPT* as alternator load. */
function isDcDcCharger(def: ComponentDefinition): boolean {
  if (def.category !== "charger") return false;
  const dcIn = def.terminals.some(
    (t) => t.direction === "input" && t.polarity === "positive" && !t.id.startsWith("pv"),
  );
  const dcOut = def.terminals.some((t) => t.direction === "output" && t.polarity === "positive");
  return dcIn && dcOut;
}

function isAlternatorOutput(def: ComponentDefinition, term: Terminal): boolean {
  if (term.direction !== "output" || term.polarity !== "positive") return false;
  if (def.category === "engine") return true;
  return def.category === "source" && /alternator/i.test(`${def.id} ${def.name}`);
}

/**
 * DC-DC chargers fed by an alternator against its rating. Wired, not counted:
 * a DC-DC only loads the alternators its input can reach through the graph,
 * so a charger on the house side or a solar controller never lands here.
 */
export function detectDcDcVsAlternator(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const graph = buildGraph(system, defLookup);
  const alternators: Array<{ nodeId: string; rated: number; reach: Set<Vertex> }> = [];
  for (const n of system.nodes) {
    if (n.kind !== "component") continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (!def?.ampOutput) continue;
    for (const t of def.terminals) {
      if (!isAlternatorOutput(def, t)) continue;
      alternators.push({ nodeId: n.id, rated: def.ampOutput, reach: reachable(graph, v(n.id, t.id)) });
      break;
    }
  }
  if (alternators.length === 0) return [];

  let alternatorRated = 0;
  let dcDcDraw = 0;
  const dcDcNodeIds: string[] = [];
  const counted = new Set<string>();
  for (const n of system.nodes) {
    if (n.kind !== "component") continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (!def || !isDcDcCharger(def)) continue;
    const inputs = def.terminals.filter(
      (t) => t.direction === "input" && t.polarity === "positive" && !t.id.startsWith("pv"),
    );
    const feeders = alternators.filter((a) =>
      inputs.some((t) => a.reach.has(v(n.id, t.id))),
    );
    if (feeders.length === 0) continue;
    dcDcDraw += def.chargeAmpsTypical ?? def.ampOutput ?? 0;
    dcDcNodeIds.push(n.id);
    for (const a of feeders) {
      if (counted.has(a.nodeId)) continue;
      counted.add(a.nodeId);
      alternatorRated += a.rated;
    }
  }
  if (dcDcDraw === 0 || alternatorRated === 0) return [];

  const ratio = dcDcDraw / alternatorRated;
  if (ratio > 0.5) {
    return [
      {
        code: "dcdc-overloads-alternator",
        severity: ratio > 0.75 ? "critical" : "warning",
        title: {
          key: "diag.dcdc-overloads-alternator.title",
          vars: { pct: Math.round(ratio * 100) },
        },
        message: {
          key: "diag.dcdc-overloads-alternator.message",
          vars: {
            draw: Math.round(dcDcDraw),
            rated: Math.round(alternatorRated),
          },
        },
        nodeIds: dcDcNodeIds,
        fix: { key: "diag.dcdc-overloads-alternator.fix" },
      },
    ];
  }
  return [];
}

/**
 * Compare charge sources with declared maxima, recommendations or planning
 * thresholds. Chemistry-only fallbacks (lead 0.3C, LFP 0.5C) are advisory.
 * A battery with neither chemistry nor charge data is left out —
 * `battery-chemistry-unknown` says so — rather than judged by the lead rule,
 * which put a 0.42 C warning on two custom 280 Ah LFPs rated 160 A each.
 */
export function detectChargerVsAcceptance(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  // Raw diagnostic edges retain dangling endpoints so connectivity checks can
  // report them. They cannot establish an actual charging path through a
  // nonexistent stud; use the same valid DC graph as complete circuit sizing.
  const graph = buildDcCircuitTopology(system, defLookup).graph;
  const regions = new Map<Vertex, number>();
  let nextRegion = 0;
  const region = (vertex: Vertex): number => {
    const existing = regions.get(vertex);
    if (existing != null) return existing;
    const id = nextRegion++;
    for (const member of reachable(graph, vertex)) regions.set(member, id);
    return id;
  };
  const pair = (positive: Vertex, negative: Vertex) => `${region(positive)}|${region(negative)}`;
  const definitions = new Map(system.nodes.flatMap(node => {
    if (node.kind !== "component") return [];
    const def = defLookup(node.definitionId, node.customDefinition);
    return def ? [[node.id, def] as const] : [];
  }));
  type Bank = { ranges: BatteryStack[]; ports: Array<{ chargerId: string; limit: number }> };
  const banks = new Map<string, Bank>();
  for (const range of computeBatterySourceRanges(system, defLookup, graph)) {
    const key = pair(range.topPositives[0], range.bottomNegatives[0]);
    const bank = banks.get(key) ?? { ranges: [], ports: [] };
    bank.ranges.push(range);
    banks.set(key, bank);
  }
  for (const node of system.nodes) {
    const def = definitions.get(node.id);
    if (!def || (def.category !== "charger" && !isDirectDcSource(def))) continue;
    for (const group of dcChargingPortGroups(def)) {
      const positive = group.find(t => t.polarity === "positive");
      const negative = group.find(t => t.polarity !== "positive");
      if (!positive || !negative) continue;
      const key = pair(v(node.id, positive.id), v(node.id, negative.id));
      if (banks.has(key)) {
        const rated = def.ampOutput ?? def.chargeAmpsTypical ?? 0;
        const portLimit = outputPortCurrent(def, positive.id, rated);
        banks.get(key)!.ports.push({ chargerId: node.id, limit: portLimit });
      }
    }
  }

  const contributions = new Map<string, Map<string, Array<{ fraction: number; limit: number }>>>();
  for (const bank of banks.values()) {
    if (bank.ports.length === 0) continue;
    const fractions = batteryCurrentShares(bank.ranges, id => definitions.get(id)?.capacityAh ?? 0);
    const unknownSharing = bank.ranges.some(range => range.batteryNodeIds.some(id => (definitions.get(id)?.capacityAh ?? 0) <= 0));
    for (const [id, fraction] of fractions) {
      const byCharger = contributions.get(id) ?? new Map<string, Array<{ fraction: number; limit: number }>>();
      for (const port of bank.ports) {
        const possibilities = byCharger.get(port.chargerId) ?? [];
        possibilities.push({ fraction: unknownSharing ? 1 : fraction, limit: port.limit });
        byCharger.set(port.chargerId, possibilities);
      }
      contributions.set(id, byCharger);
    }
  }
  const charging = new Map([...contributions].map(([id, byCharger]) => {
    let amps = 0;
    for (const [chargerId, ports] of byCharger) {
      const def = definitions.get(chargerId)!;
      let remaining = def.ampOutput ?? def.chargeAmpsTypical ?? 0;
      // Maximize this battery's possible current while respecting each port
      // and the one shared charger rating. Equal sharing is not guaranteed;
      // nor may two outputs each spend the entire shared rating.
      for (const port of [...ports].sort((a, b) => b.fraction - a.fraction)) {
        const allocated = Math.min(remaining, port.limit);
        amps += allocated * port.fraction;
        remaining -= allocated;
      }
    }
    return [id, { amps, chargerIds: new Set(byCharger.keys()) }] as const;
  }));
  const issues = new Map<string, Issue>();
  for (const [batteryId, current] of charging) {
    const def = definitions.get(batteryId)!;
    if (def.chemistry == null && def.maxChargeA == null && def.recommendedChargeA == null && def.chargePlanningA == null) continue;
    const totalAh = def.capacityAh ?? 0;
    const isLead = def.chemistry !== "lifepo4";
    const basis = chargeAdviceBasis(findNode(system, batteryId), def);
    const acceptA = chargeAdviceThreshold(def);
    const totalChargeA = current.amps;
    if (totalChargeA <= acceptA + 1e-6) continue;
    // Compare the unchanged charge threshold with the current through
    // that same battery, including simultaneous whole-bank and midpoint feeds.
    // Preserve enough precision that an actual exceedance never reads "22 A
    // exceeds 22 A" after parallel-current allocation.
    const precision = Math.round(totalChargeA) > Math.round(acceptA) ? 0 : Math.min(6, Math.max(1, Math.ceil(-Math.log10(totalChargeA - acceptA)) + 1));
    const display = (amps: number) => Number(amps.toFixed(precision));
    const vars = { charge: display(totalChargeA), ah: totalAh, limit: totalAh > 0 ? Math.round((acceptA / totalAh) * 100) / 100 : 0, accept: display(acceptA) };
    const groupKey = JSON.stringify([vars, isLead, basis]);
    const existing = issues.get(groupKey);
    if (existing) {
      existing.nodeIds = [...new Set([...(existing.nodeIds ?? []), ...current.chargerIds, batteryId])];
      continue;
    }
    issues.set(groupKey, {
      code: "charge-exceeds-acceptance",
      severity: basis === "declared" && isLead ? "warning" : "info",
      title: {
        key: basis === "manufacturer-recommended" ? "diag.charge-exceeds-acceptance.title_recommended"
          : basis === "catalog-planning" ? "diag.charge-exceeds-acceptance.title_planning"
            : totalAh > 0 ? "diag.charge-exceeds-acceptance.title" : "diag.charge-exceeds-acceptance.title_current",
        vars: { cRate: totalAh > 0 ? Math.round((totalChargeA / totalAh) * 100) / 100 : 0, charge: Math.round(totalChargeA) },
      },
      message: {
        key: basis === "manufacturer-recommended" ? "diag.charge-exceeds-acceptance.message_recommended"
          : basis === "catalog-planning" ? "diag.charge-exceeds-acceptance.message_planning"
            : basis === "declared" ? "diag.charge-exceeds-acceptance.message_specified" : "diag.charge-exceeds-acceptance.message",
        vars,
      },
      nodeIds: [...current.chargerIds, batteryId],
      fix: {
        key: basis === "manufacturer-recommended" ? "diag.charge-exceeds-acceptance.fix_recommended"
          : basis === "catalog-planning" ? "diag.charge-exceeds-acceptance.fix_planning"
            : basis === "declared" ? "diag.charge-exceeds-acceptance.fix_specified"
              : isLead ? "diag.charge-exceeds-acceptance.fix_lead" : "diag.charge-exceeds-acceptance.fix_lfp",
      },
    });
  }
  return [...issues.values()];
}

/** A path can brown out its load while no single cable on it is flagged. */
export function detectCumulativePathVDrop(
  system: System,
  defLookup: DefLookup,
  autoSizeTargetPct: number = DEFAULT_AUTO_SIZE_VDROP_TARGET,
): Issue[] {
  const specOf = makeDiagnosticCableSpecGetter(system, defLookup, autoSizeTargetPct);
  const allowances = computeVdropAllowances(system, defLookup, autoSizeTargetPct);
  const worstByLoad = new Map<Vertex, { drop: number; conditional: boolean; issue: Issue }>();

  const pvCircuits = computePvCurrentAnalysis(indexNodesById(system.nodes), system.cables, defLookup).circuits;
  const pv = new Map<LoadCircuit, typeof pvCircuits[number]>(pvCircuits.map(c => [c, c]));
  for (const circuit of [...collectLoadCircuits(system, defLookup), ...pvCircuits]) {
    const lv = circuit.loadVertex;
    const cableIds = [...new Set([...circuit.pos, ...circuit.neg])];
    // ABYC E-11.14.2.6: actual positive + return distance. Only infer a
    // mirrored return when the drawing does not contain one.
    const weight = circuit.complete ? 0.5 : 1;

    let totalDrop = 0;
    let provisionalDrop = 0;
    let pathNominalV = pv.get(circuit)?.nominalV ?? 0;
    let crossesAc = false;
    let unresolvedCurrent = false;
    const resolvedCableIds: string[] = [];
    const provisionalCableIds: string[] = [];
    for (const id of cableIds) {
      const c = findCable(system, id);
      if (!c) continue;
      const spec = specOf(c);
      if (spec.isAc) {
        crossesAc = true;
        break;
      }
      if (pathNominalV === 0) pathNominalV = spec.nominalV;
      if (currentNeedsVerification(spec) || spec.sizingVerificationRequired) {
        // Unknown source allocation or provisional copper cannot establish
        // a definite contribution. Omitting those terms gives a lower bound
        // on the design-envelope sum, not physical simultaneous loss: the
        // per-conductor maxima may occur in different operating cases.
        unresolvedCurrent = true;
        // Keep a provisional estimate visible as a verification request,
        // not a lower bound. Source-choice-only bounds have their own grouped
        // notice. Untyped starter current cannot establish sustained loss.
        const hasUntypedStartingDuty = spec.sustainedCurrent == null &&
          (spec.isCranking || (spec.startingCurrent ?? 0) > 0);
        if (spec.currentBasis === "provisional-fallback" && !hasUntypedStartingDuty) {
          provisionalDrop += voltageDrop(sustainedCableCurrent(spec), c.lengthM, spec.wire.mm2) * weight;
          provisionalCableIds.push(id);
        }
        continue;
      }
      resolvedCableIds.push(id);
      // This is a sustained load-circuit check. Starter-loop sizing applies
      // its own peak/duration policy; it must not become a continuous drop
      // claim for every cabin load sharing the battery return.
      totalDrop += voltageDrop(sustainedCableCurrent(spec), c.lengthM, spec.wire.mm2) * weight;
    }
    // A path through an AC conductor is not a DC circuit this can budget.
    if (crossesAc) continue;
    // PV routes require the same complete current evidence as automatic
    // sizing. Shared unresolved DC returns already have verification notices.
    if (pv.has(circuit) && unresolvedCurrent) continue;
    if (pathNominalV === 0) pathNominalV = SYSTEM_BUS_V;
    // Thresholds ride the circuit's own ABYC E-11.14.2.6 allowance, never one
    // fixed number: 6 % is fine on a cabin light and double budget on a plotter.
    const allowancePct = pv.has(circuit)
      ? Math.min(autoSizeTargetPct, ...cableIds.map(id => allowances.byCable.get(id) ?? autoSizeTargetPct))
      : allowances.byLoadVertex.get(lv) ?? autoSizeTargetPct;
    let totalDropPct = (totalDrop / pathNominalV) * 100;
    const conditional = totalDropPct <= allowancePct + 1e-9 && provisionalDrop > 0;
    if (conditional) {
      totalDrop += provisionalDrop;
      totalDropPct = (totalDrop / pathNominalV) * 100;
    }
    if (totalDropPct > allowancePct + 1e-9) {
      const { nodeId } = splitV(lv);
      const node = findNode(system, nodeId);
      const def =
        node?.kind === "component"
          ? defLookup(node.definitionId, node.customDefinition)
          : undefined;
      const label = node?.label ?? def?.shortName ?? def?.name ?? diagnosticLabel("diag.label.load");
      // Source routes are alternatives. Report the worst route once per load
      // instead of repeating the same warning for every parallel battery.
      const previous = worstByLoad.get(lv);
      // Prefer an independently supported design exceedance over a larger
      // provisional estimate from another source route.
      if (previous && ((!previous.conditional && conditional) ||
        (previous.conditional === conditional && previous.drop >= totalDropPct))) continue;
      worstByLoad.set(lv, { drop: totalDropPct, conditional, issue: {
        code: pv.has(circuit) ? "pv-cumulative-vdrop" : "cumulative-vdrop",
        severity: "warning",
        title: {
          key: pv.has(circuit) ? "diag.pv-cumulative-vdrop.title" : conditional ? "diag.cumulative-vdrop.title_provisional"
            : unresolvedCurrent ? "diag.cumulative-vdrop.title_lower_bound" : "diag.cumulative-vdrop.title",
          vars: { label, pct: totalDropPct.toFixed(1) },
        },
        message: {
          key: pv.has(circuit) ? "diag.pv-cumulative-vdrop.message" : conditional ? "diag.cumulative-vdrop.message_provisional"
            : unresolvedCurrent ? "diag.cumulative-vdrop.message_lower_bound" : "diag.cumulative-vdrop.message",
          vars: { label, drop: totalDrop.toFixed(2), allowance: allowancePct },
        },
        nodeIds: [nodeId],
        cableIds: conditional ? [...resolvedCableIds, ...provisionalCableIds] : resolvedCableIds,
        fix: { key: pv.has(circuit) ? "diag.pv-cumulative-vdrop.fix" : conditional ? "diag.cumulative-vdrop.fix_provisional" : "diag.cumulative-vdrop.fix" },
      } });
    }
  }
  return [...worstByLoad.values()].map(({ issue }) => issue);
}

/**
 * One cable, sized the way the rest of the engine sizes it — the only way a
 * detector may size a cable. A bare `computeCableSpec` drops five whole-system
 * hints and silently judges a different conductor than the canvas draws.
 *
 * Build the shared whole-system hints once for all cables in this diagnostic pass.
 */
export function makeDiagnosticCableSpecGetter(
  system: System,
  defLookup: DefLookup,
  autoSizeTargetPct: number = DEFAULT_AUTO_SIZE_VDROP_TARGET,
  simulate?: SimulateContext,
): (cable: Cable) => CableSpec {
  return makeSystemCableSpecGetter(system, defLookup, simulate, autoSizeTargetPct);
}

/** Starting loops have their own peak-current budget. The sustained load
 * detector cannot report a table limit or pinned wire that misses this target.
 * This requests verification against the fitted engine's requirements; the
 * planner target is not a universal installation/compliance limit. */
export function detectCrankingVoltageDrop(
  system: System,
  defLookup: DefLookup,
  autoSizeTargetPct: number = DEFAULT_AUTO_SIZE_VDROP_TARGET,
): Issue[] {
  const specOf = makeDiagnosticCableSpecGetter(system, defLookup, autoSizeTargetPct);
  const worst = new Map<Vertex, { pct: number; issue: Issue }>();
  for (const circuit of collectCrankingCircuits(system, defLookup)) {
    if (!circuit.complete) continue;
    const cableIds = [...new Set([...circuit.pos, ...circuit.neg])];
    let drop = 0;
    let nominalV = 0;
    let supported = cableIds.length > 0;
    for (const id of cableIds) {
      const cable = findCable(system, id);
      if (!cable) { supported = false; break; }
      const spec = specOf(cable);
      // Existing current-model/source-allocation notices own uncertain bounds.
      if (spec.isAc || currentNeedsVerification(spec) || spec.sizingVerificationRequired) { supported = false; break; }
      nominalV ||= spec.nominalV;
      // voltageDrop takes a one-way length and doubles it; each drawn physical
      // conductor contributes once, including source-string series jumpers.
      drop += voltageDrop(spec.current, cable.lengthM, spec.wire.mm2) * 0.5;
    }
    if (!supported || nominalV <= 0) continue;
    const pct = drop / nominalV * 100;
    if (pct <= resolveDesignProfile(system).policy.voltageDrop.startingPct + 1e-9) continue;
    if ((worst.get(circuit.loadVertex)?.pct ?? 0) >= pct) continue;
    const { nodeId } = splitV(circuit.loadVertex);
    worst.set(circuit.loadVertex, { pct, issue: {
      code: "cranking-vdrop-unverified", severity: "warning", nodeIds: [nodeId], cableIds,
      title: { key: "diag.cranking-vdrop-unverified.title" },
      message: { key: "diag.cranking-vdrop-unverified.message", vars: { pct: pct.toFixed(1) } },
      fix: { key: "diag.cranking-vdrop-unverified.fix" },
    } });
  }
  return [...worst.values()].map(({ issue }) => issue);
}

/** A cable whose path-walked demand exceeds its fuse rating or its ampacity. */
export function detectUnderRatedUpstream(
  system: System,
  defLookup: DefLookup,
  autoSizeTargetPct: number = DEFAULT_AUTO_SIZE_VDROP_TARGET,
): Issue[] {
  const issues: Issue[] = [];
  const nodesById = indexNodesById(system.nodes);
  const demands = computeCableDemands(system, defLookup);
  const specOf = makeDiagnosticCableSpecGetter(system, defLookup, autoSizeTargetPct);

  for (const c of system.cables) {
    const demand = demands.get(c.id);
    if (!demand || demand.continuous <= 0) continue;
    const spec = specOf(c);
    // An AC run is sized to its inlet's breaker and carries no DC demand.
    if (spec.isAc || spec.continuous <= 0 || spec.sourceAllocationDependent || spec.sizingVerificationRequired) continue;
    // The effective fuse, not the cable's own override: a battery-post MRBF or
    // an inline holder's rating is what actually opens. Crosby shipped "clean"
    // with a 40 A MRBF under a 47 A house trunk and another in a 280 A
    // cranking path because only `fuseAmpOverride` was checked.
    const effective = getEffectiveCableFuse(c, nodesById, defLookup);
    const fuse = effective.ratingA;
    const fuseInherited = fuse != null && effective.source.rating !== "cable";

    // OVERLOAD_TOLERANCE keeps rounding noise from flipping the warning on.
    const provisional = spec.currentBasis === "provisional-fallback";
    const hasStartingDuty = spec.isCranking || (spec.startingCurrent ?? demand.startingCurrent ?? 0) > 0;
    // Normal and starting extrema can occur in different cases. Never obtain
    // sustained demand by subtracting the maximum starter current from the
    // maximum total; use the signed ledger's independent sustained envelope.
    const sustainedCurrent = spec.sustainedCurrent ?? (hasStartingDuty ? undefined : spec.continuous);
    const sustainedOverload = !provisional && sustainedCurrent != null &&
      fuse != null && sustainedCurrent > fuse * OVERLOAD_TOLERANCE;
    const needsFuseVerification = !sustainedOverload && (provisional || hasStartingDuty);
    const fuseDemand = sustainedOverload ? sustainedCurrent : spec.continuous;
    const fuseUnderRated = fuse != null && fuseDemand > fuse * OVERLOAD_TOLERANCE;
    // ABYC E-11 / NEC 310.15 1.25× continuous-load headroom. Cranking circuits
    // are exempt (the current is brief) and compare against raw continuous.
    const isCranking = spec.isCranking;
    const wireHeadroom = isCranking ? 1 : resolveDesignProfile(system).policy.continuousLoadHeadroom;
    // A parallel bank tie can carry brief starter current without inheriting
    // the starter's protection exemption. Use the same duty-aware ampacity
    // that selected the conductor, including sustained accessory loads.
    const requiredAmpacity = spec.requiredAmpacity ?? demand.continuous * wireHeadroom;
    // A fuse at or under the wire's ampacity caps what the conductor can carry,
    // so the wire only has to hold the fuse rating. ABYC E-11.10.1.1.1
    // EXCEPTION 1 exempts a cranking conductor, which then routinely carries a
    // fuse above its ampacity — hence `wireHeadroom` 1 rather than skipping.
    const wireProtectedByFuse =
      fuse != null && fuse <= spec.wire.ampacity * OVERLOAD_TOLERANCE;
    const wireUnderRated =
      !wireProtectedByFuse &&
      requiredAmpacity >
        spec.wire.ampacity * OVERLOAD_TOLERANCE;

    if (fuseUnderRated) {
      // Re-run the engine with the override cleared rather than sizing here, so
      // the fix button's rating is the one the inspector shows for this cable.
      const recommendedSpec = specOf({ ...c, fuseAmpOverride: null });
      // An inherited rating lives on the post or holder; a cable-level fix
      // would add a second fuse rather than replace the one that trips.
      const inheritsWithoutOverride = getEffectiveCableFuse({ ...c, fuseAmpOverride: null }, nodesById, defLookup).ratingA != null;
      const newFuse = !needsFuseVerification && !recommendedSpec.sizingVerificationRequired &&
        !recommendedSpec.manufacturerInstallationUnverified && !recommendedSpec.fuseRecommendationUnavailableReason &&
        !fuseInherited && !inheritsWithoutOverride && recommendedSpec.fuse > fuse
        ? recommendedSpec.fuse : 0;
      issues.push({
        code: "fuse-trips-under-load",
        severity: "warning",
        title: {
          key: needsFuseVerification ? "diag.fuse-trips-under-load.title_verify" : "diag.fuse-trips-under-load.title",
          vars: { rating: fuse, load: Math.round(fuseDemand) },
        },
        message: {
          key: needsFuseVerification
            ? provisional ? "diag.fuse-trips-under-load.message_provisional" : "diag.fuse-trips-under-load.message_starting"
            : "diag.fuse-trips-under-load.message",
          vars: { load: Math.round(fuseDemand), rating: fuse },
        },
        cableIds: [c.id],
        nodeIds: [c.fromNodeId, c.toNodeId],
        fix: newFuse > 0 ? {
          key: "diag.fuse-trips-under-load.fix",
          vars: { target: newFuse },
        } : { key: "diag.fuse-trips-under-load.fix_verify" },
        fixAction: newFuse
          ? {
              kind: "set-cable-fuse",
              cableId: c.id,
              rating: newFuse,
              label: {
                key: "diag.fix.upgrade_fuse_to",
                vars: { rating: newFuse },
              },
            }
          : undefined,
      });
    } else if (wireUnderRated) {
      // The cable's own nominal voltage: a PV-string run sizes against string
      // Vmp, not 12 V.
      const recommendedSpec = specOf({ ...c, wireSizeMm2Override: null });
      const recWire = recommendedSpec.wire;
      // Selection saturates at the largest catalog conductor. A larger size
      // is not a fix unless it actually meets this circuit's requirements.
      const recommendedDropPct = recommendedSpec.vDropPct * (hasStartingDuty && recommendedSpec.continuous > 0
        ? recommendedSpec.current / recommendedSpec.continuous : 1);
      const allowedDropPct = hasStartingDuty ? resolveDesignProfile(system).policy.voltageDrop.startingPct : recommendedSpec.vdropAllowancePct;
      const canUpgradeWire = !provisional && !recommendedSpec.sizingVerificationRequired &&
        !recommendedSpec.manufacturerInstallationUnverified && recWire.mm2 > spec.wire.mm2 &&
        recWire.ampacity >= requiredAmpacity && recommendedDropPct <= allowedDropPct;
      // At the top of the wire table a fuse ≤ ampacity is the only remediation
      // left; `recommendFuse` falls back to the largest rating ≤ ampacity.
      const fuseCandidate = !canUpgradeWire
        ? recommendFuse(spec.continuous, spec.wire.ampacity, spec.continuous, resolveDesignProfile(system).policy)
        : 0;
      const candidateSpec = fuseCandidate > 0
        ? applyFuseBoardCableLimits(c, nodesById, defLookup, { ...spec, fuse: fuseCandidate })
        : spec;
      // Never offer a fuse on a negative or ground leg (E-11.10.1 protects the
      // ungrounded conductor); the inspector hides fuses there for the same reason.
      const fuseFix =
        !provisional &&
        !spec.manufacturerInstallationUnverified && !spec.fuseRecommendationUnavailableReason &&
        !candidateSpec.fuseRecommendationUnavailableReason &&
        resolveCablePolarity(c, nodesById, system.cables) !== "negative" &&
        fuseCandidate > 0 &&
        fuseCandidate >= spec.continuous &&
        fuseCandidate <= spec.wire.ampacity
          ? fuseCandidate
          : 0;
      const required = Math.ceil(requiredAmpacity);
      issues.push({
        code: "wire-undersized-for-load",
        severity: "warning",
        title: {
          key: provisional ? "diag.wire-undersized-for-load.title_provisional" : "diag.wire-undersized-for-load.title",
          vars: {
            required,
            ampacity: spec.wire.ampacity,
          },
        },
        message: {
          key: provisional ? "diag.wire-undersized-for-load.message_provisional" : "diag.wire-undersized-for-load.message",
          vars: {
            mm2: spec.wire.mm2,
            ampacity: spec.wire.ampacity,
            load: Math.round(spec.continuous),
            required,
            headroomMul: wireHeadroom,
          },
        },
        cableIds: [c.id],
        nodeIds: [c.fromNodeId, c.toNodeId],
        fix: {
          key: provisional ? "diag.wire-undersized-for-load.fix_verify"
            : canUpgradeWire ? "diag.wire-undersized-for-load.fix_wire"
            : fuseFix ? "diag.wire-undersized-for-load.fix" : "diag.wire-undersized-for-load.fix_manual",
          vars: {
            required,
            ampacity: spec.wire.ampacity,
          },
        },
        fixAction: canUpgradeWire
          ? {
              kind: "set-cable-wire",
              cableId: c.id,
              mm2: recWire.mm2,
              label: {
                key: "diag.fix.upgrade_wire_to",
                vars: { mm2: recWire.mm2 },
              },
            }
          : fuseFix
            ? {
                kind: "set-cable-fuse",
                cableId: c.id,
                rating: fuseFix,
                label: {
                  key: "diag.fix.upgrade_fuse_to",
                  vars: { rating: fuseFix },
                },
              }
            : undefined,
      });
    }
  }
  return issues;
}

/** A provisional current estimate must not look like a verified conductor
 * model. Missing feeds/returns are reported by connectivity diagnostics; this
 * warning is for connected arrangements the current solver cannot resolve. */
export function detectUnverifiedCurrentModel(system: System, defLookup: DefLookup): Issue[] {
  const analysis = computeCableDemandAnalysis(system, defLookup);
  const unsupported = analysis.unresolvedComponents.filter(item =>
    ["parallel-paths", "source-choice-limit", "source-route-limit", "source-range-limit", "shared-port-rating", "unsupported-component-category", "ambiguous-current-role", "unsupported-pv-array", "one-way-device", "restricted-device-use", "converter-current-coupling"].includes(item.reason),
  );
  // A passive branch can have a local fuse/terminal estimate without a
  // modeled device demand. The shared getter marks that estimate provisional;
  // make the gap visible here as well as in the inspector and exports.
  const specOf = makeSystemCableSpecGetter(system, defLookup);
  const unmodeledCables = system.cables.filter(cable => {
    const spec = specOf(cable);
    // Profile coverage is reported separately; it does not invalidate a
    // resolved current merely because conductor/routing rules are unavailable.
    return !analysis.demands.has(cable.id) && !analysis.fallbackCableIds.has(cable.id) &&
      (spec.currentBasis === "provisional-fallback" || spec.sourceAllocationDependent);
  });
  if (!unsupported.length && !unmodeledCables.length) return [];
  const nodeIds = [...new Set([
    ...unsupported.map(item => item.nodeId),
    ...unmodeledCables.flatMap(cable => [cable.fromNodeId, cable.toNodeId]),
  ])].sort();
  const reasons = [...unsupported, ...unmodeledCables.flatMap(c =>
    [c.fromNodeId, c.toNodeId].map(nodeId => ({ nodeId, reason: "unmodeled-branch" })))];
  const seen = new Set<string>();
  const details = reasons.flatMap(({ nodeId, reason }) => {
    const key = `${nodeId}:${reason}`;
    if (seen.has(key)) return [];
    seen.add(key);
    const node = system.nodes.find(n => n.id === nodeId);
    const def = node?.kind === "component" ? defLookup(node.definitionId, node.customDefinition) : undefined;
    const label = node?.label ?? def?.shortName ?? def?.name ?? nodeId;
    return [{ key: `diag.current-model-unverified.reason.${reason}`, vars: { label } }];
  });
  return [{
    code: "current-model-unverified", severity: "warning", nodeIds,
    cableIds: [...new Set([...analysis.fallbackCableIds, ...unmodeledCables.map(c => c.id)])].sort(),
    title: { key: "diag.current-model-unverified.title" },
    message: { key: "diag.current-model-unverified.message", vars: { count: nodeIds.length } },
    fix: { key: "diag.current-model-unverified.fix" },
    details,
  }];
}

/** Source alternatives give a safe sizing bound without proving allocation.
 * One grouped request replaces definite overload claims on those branches. */
export function detectSourceCurrentAllocationUnverified(system: System, defLookup: DefLookup): Issue[] {
  const analysis = computeCableDemandAnalysis(system, defLookup);
  const cableIds = [...analysis.sourceAllocationDependentCableIds].sort();
  if (!cableIds.length) return [];
  return [{
    code: "source-current-allocation-unverified", severity: "warning",
    nodeIds: [...analysis.sourceAllocationDependentNodeIds].sort(), cableIds,
    title: { key: "diag.source-current-allocation-unverified.title" },
    message: { key: "diag.source-current-allocation-unverified.message", vars: { count: cableIds.length } },
    fix: { key: "diag.source-current-allocation-unverified.fix" },
  }];
}

export function runSizingDiagnostics(
  system: System,
  defLookup: DefLookup,
  autoSizeTargetPct: number = DEFAULT_AUTO_SIZE_VDROP_TARGET,
): Issue[] {
  const combinedDischarge = detectCombinedBatteryDischarge(system, defLookup);
  const inverterDischarge = detectInverterVsBattery(system, defLookup).filter(issue => {
    const inverterIds = issue.nodeIds ?? [];
    const covering = combinedDischarge.filter(combined => inverterIds.length > 0 &&
      inverterIds.every(id => combined.nodeIds?.includes(id)));
    // Keep the established critical inverter severity without repeating the
    // same declared-limit finding beside the complete-route demand check.
    if (issue.severity === "critical") for (const combined of covering) combined.severity = "critical";
    return covering.length === 0;
  });
  return [
    ...detectUnverifiedCurrentModel(system, defLookup),
    ...detectSourceCurrentAllocationUnverified(system, defLookup),
    ...detectInstallationLimits(system, defLookup),
    ...inverterDischarge,
    ...combinedDischarge,
    ...detectDcDcVsAlternator(system, defLookup),
    ...detectChargerVsAcceptance(system, defLookup),
    ...detectCumulativePathVDrop(system, defLookup, autoSizeTargetPct),
    ...detectChargerPathVDrop(system, defLookup, autoSizeTargetPct),
    ...detectCrankingVoltageDrop(system, defLookup, autoSizeTargetPct),
    ...detectUnderRatedUpstream(system, defLookup, autoSizeTargetPct),
  ];
}
